"use client";

import { useEffect, useRef } from "react";

import {
  trackPaymentOutcomeEvent,
  type PaymentExpiryReason,
} from "@/lib/payments/payment-outcome-analytics";
import { trackAdPurchase } from "@/lib/payments/ad-purchase";
import { trackOpenAiAdsCheckoutStarted } from "@/lib/openai-ads";

interface UsePaymentOutcomeTrackingOptions {
  paymentId: string;
  isCompleted: boolean;
  isFailedState: boolean;
  isExpiredState: boolean;
  /**
   * Why the QR is being retired. `countdown` is our own 30-minute clock
   * lapsing; `qr_failed` is the provider reporting the code dead while another
   * rail is still payable. Both read as expiry to the buyer, but they are
   * different leaks, so the event carries which.
   */
  expiryReason: PaymentExpiryReason;
  tier: string | null;
  amount: number;
  isGuest: boolean;
  onSuccess: (paymentId: string) => void;
  onTerminalState?: (() => void) | undefined;
}

export interface PaymentOutcomeTracking {
  /**
   * Re-arms the failure and expiry latches.
   *
   * Reissuing a QR gives the same payment a fresh chance to succeed or lapse,
   * and the new outcome is a real second event rather than a repeat of the one
   * already reported.
   */
  resetTerminalTracking: () => void;
}

/**
 * Fires each payment outcome exactly once.
 *
 * Every one of these is reached by a polling render that can repeat, so each
 * outcome is latched behind its own ref. Without that, a buyer sitting on an
 * expired QR emits an expiry event every poll and the funnel counts one
 * abandonment many times.
 */
export function usePaymentOutcomeTracking({
  paymentId,
  isCompleted,
  isFailedState,
  isExpiredState,
  expiryReason,
  tier,
  amount,
  isGuest,
  onSuccess,
  onTerminalState,
}: UsePaymentOutcomeTrackingOptions): PaymentOutcomeTracking {
  const hasSucceededRef = useRef(false);
  const failedTrackedRef = useRef(false);
  const expiredTrackedRef = useRef(false);

  useEffect(() => {
    // The payment step only renders once a checkout exists, so its first
    // render is the ChatGPT Ads checkout_started moment. Latched per payment.
    trackOpenAiAdsCheckoutStarted({ paymentId, amount, tier });
  }, [paymentId, amount, tier]);

  useEffect(() => {
    if (isCompleted && !hasSucceededRef.current) {
      hasSucceededRef.current = true;
      // The only browser-visible purchase signal: the GTM UET tag and the
      // OpenAI pixel receive it. Every paid QRIS, virtual account and guest
      // flow ends here.
      trackAdPurchase({ paymentId, amount, tier });
      onSuccess(paymentId);
    }
  }, [isCompleted, paymentId, amount, tier, onSuccess]);

  useEffect(() => {
    if (isFailedState && !failedTrackedRef.current) {
      failedTrackedRef.current = true;
      onTerminalState?.();
      trackPaymentOutcomeEvent("payment_failed", {
        paymentId,
        tier,
        amount,
        guest: isGuest,
      });
    }
  }, [isFailedState, paymentId, tier, amount, isGuest, onTerminalState]);

  useEffect(() => {
    if (isExpiredState && !expiredTrackedRef.current) {
      expiredTrackedRef.current = true;
      onTerminalState?.();
      trackPaymentOutcomeEvent(
        "payment_expired",
        { paymentId, tier, amount, guest: isGuest },
        { reason: expiryReason },
      );
    }
  }, [
    isExpiredState,
    expiryReason,
    paymentId,
    tier,
    amount,
    isGuest,
    onTerminalState,
  ]);

  return {
    resetTerminalTracking: () => {
      failedTrackedRef.current = false;
      expiredTrackedRef.current = false;
    },
  };
}
